/**
 * Email Triggers Hook
 *
 * Custom hook for managing email trigger templates data
 * Follows the pattern from useSmsTemplates.ts
 */

import { useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import EmailTriggerService from '@/api/emailTriggers';
import type { EmailTriggerTemplate, UseTriggerTemplates } from '@/types/email-triggers';

// Re-export types for convenience
export type { EmailTriggerTemplate, UseTriggerTemplates };

/**
 * Hook for fetching email trigger templates
 */
export const useEmailTriggerTemplates = (): UseTriggerTemplates => {
  const [templates, setTemplates] = useState<EmailTriggerTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        // Fetch from API endpoint
        const apiTemplates = await EmailTriggerService.getTriggerTemplates();

        // Transform API response to match EmailTriggerTemplate interface
        const transformedTemplates: EmailTriggerTemplate[] = apiTemplates.map(template => ({
          id: template.id,
          name: template.name,
          description: template.description,
          category: template.category,
          trigger_type: template.trigger_type,
          default_conditions: template.default_conditions,
          default_logic: template.default_logic || 'AND',
          default_time_delay: template.default_time_delay,

          // System template reference fields
          system_template_id: template.system_template_id || null,
          system_template_name: template.system_template_name,

          personalization_fields: template.personalization_fields || [],
          default_priority: template.default_priority,
          default_max_sends_per_customer: template.default_max_sends_per_customer,
          default_cooldown_days: template.default_cooldown_days,
          use_cases: template.use_cases || []
        }));

        setTemplates(transformedTemplates);
      } catch (error) {
        console.error('Error loading email trigger templates:', error);
        toast({
          title: 'Failed to load templates',
          description: 'Please try again later',
          variant: 'destructive'
        });
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  return {
    templates,
    loading
  };
};
